import { state } from '../core/state.js';
import { dayKey, esc } from '../core/text.js';
import { renderCurrency, saveProgress } from '../progress/xp.js';
import { confetti } from '../ui/confetti.js';
import { SFX, pushPop, toast } from '../ui/toast.js';
import { ADV_GROUPS, ADV_ITEMS, ADV_SLOTS, GAME, ITEM_COST, ITEM_MAX, RARITY, SLOT_CAP, SLOT_ICON, groupMaxWear, slotChip } from './data.js';
import { ENCH_COST, ENCH_MAX, G, QUEST_POOL, WEEKLY_POOL, gAddItemCopies, gEnchantItem, gEquipped, gIsEquipped, gItem, gQuest, gQuestDone, gStats, gWeekly, gWeeklyDone, questLabel } from './items.js';
import { RPG, renderAdventure } from './ui.js';
import { gUpgradeCost } from './world.js';

/* Tägliche Gratis-Truhe beim ersten Abenteuer-Besuch des Tages. */
export function gDailyChest() {
  const g = G(); const today = dayKey();
  if (g.lastFreeChest === today) return false;
  g.lastFreeChest = today; g.chests += 1; saveProgress();
  return true;
}
export const SKILL_MILESTONES = [10, 20, 50, 100, 200, 500, 1000];
export function gBuyUpgrade(key) {
  const cost = gUpgradeCost(key);
  if (state.progress.coins < cost) { toast('Nicht genug Wissensmünzen'); return false; }
  state.progress.coins -= cost; G().up[key]++;
  const lvl = G().up[key];
  if (SKILL_MILESTONES.includes(lvl)) {
    // Meilenstein-Feier + Bonus-Truhe(n)
    const bonus = lvl >= 100 ? 3 : lvl >= 50 ? 2 : 1;
    G().chests += bonus;
    SFX.win(); confetti();
    pushPop({ img: RPG(GAME.up[key].img), eyebrow: `Meilenstein · ${GAME.up[key].label} Stufe ${lvl}!`, name: 'Fähigkeit gemeistert', reward: `🎁 +${bonus} Schatztruhe${bonus > 1 ? 'n' : ''}` });
    skillMilestoneFx(key);
  } else { SFX.good(); }
  saveProgress(); renderCurrency(); renderAdventure();
  return true;
}
/* Kurzer Leucht-/Ring-Effekt auf dem Fähigkeits-Button beim Meilenstein */
export function skillMilestoneFx(key) {
  setTimeout(() => {
    const btn = document.querySelector(`.adv-up.skill-${key}`); if (!btn) return;
    btn.classList.add('milestone'); setTimeout(() => btn.classList.remove('milestone'), 1400);
    const ring = document.createElement('span'); ring.className = 'skill-ring'; btn.appendChild(ring);
    setTimeout(() => ring.remove(), 900);
  }, 40);
}
export function gOfflineGains() {
  const g = G(); if (!g.on) return null;
  const now = Date.now();
  if (!g.lastTick) { g.lastTick = now; return null; }
  const elapsedMin = (now - g.lastTick) / 60000;
  g.lastTick = now;
  if (elapsedMin < 2) return null;
  const st = gStats();
  let decay = 1;
  if (g.lastStudy) { const days = (now - g.lastStudy) / 86400000; if (days > 7) decay = 0.1; else if (days > 2) decay = 0.5; }
  const mins = Math.min(elapsedMin, GAME.offlineCapH * 60);
  const coins = Math.round(st.coinsPerMin * mins * decay);
  if (coins <= 0) return null;
  state.progress.coins += coins;
  return { coins, minutes: Math.round(mins), capped: elapsedMin > GAME.offlineCapH * 60, decay };
}
export function gRollRarity(luck) {
  const w = RARITY.map((r) => r.w);
  w[0] = Math.max(8, w[0] - luck * 4); w[2] += luck * 0.8; w[3] += luck * 0.5;
  const total = w.reduce((a, b) => a + b, 0);
  let r = Math.random() * total;
  for (let i = 0; i < RARITY.length; i++) { if (r < w[i]) return { ...RARITY[i], idx: i }; r -= w[i]; }
  return { ...RARITY[0], idx: 0 };
}
export function gRollLoot() {
  const g = G(); const st = gStats();
  const roll = Math.random();
  const itemChance = 0.62 + Math.min(0.2, st.luck * 0.02);   // Items sind jetzt die Haupt-Belohnung
  if (roll < itemChance) {
    const ids = Object.keys(ADV_ITEMS);
    const id = ids[Math.floor(Math.random() * ids.length)];
    const rar = gRollRarity(st.luck + G().charLvl * 0.15);   // höhere Lernstufe = bessere Truhen
    const isNew = !gItem(id);
    const res = gAddItemCopies(id, rar.copies);
    if (res.overflow > 0) state.progress.coins += res.overflow * 40;   // maxed → Überschuss als Münzen
    return { type: 'item', id, item: ADV_ITEMS[id], rarity: rar, level: res.level, isNew, overflow: res.overflow, copies: rar.copies };
  }
  if (roll < itemChance + 0.24) {
    const amount = Math.max(20, Math.round(st.coinsPerMin * (20 + Math.random() * 60)));
    state.progress.coins += amount; return { type: 'coins', amount };
  }
  if (roll < itemChance + 0.32) {
    const before = g.focusEnergy || 0; g.focusEnergy = Math.min(GAME.fokusMax, before + 6 + Math.floor(Math.random() * 10));
    return { type: 'focus', amount: g.focusEnergy - before };
  }
  const amount = 1 + (Math.random() < 0.2 ? 1 : 0); state.progress.gems += amount; return { type: 'gems', amount };
}
/* Item aus- bzw. anlegen (Ausrüsten). */
export function gEquip(id) {
  const g = G(); if (!gItem(id)) return;
  const slot = ADV_ITEMS[id].slot;
  if (!Array.isArray(g.equip[slot])) g.equip[slot] = g.equip[slot] ? [g.equip[slot]] : [];
  const arr = g.equip[slot];
  const i = arr.indexOf(id);
  if (i >= 0) arr.splice(i, 1);                 // schon getragen → ablegen
  else { if (arr.length >= SLOT_CAP) arr.shift(); arr.push(id); }   // voll → ältestes ersetzen
  try { SFX.flip(); } catch (e) {}
  saveProgress(); renderGearPanel(); refreshArenaStats();
}
/* Nur das Ausrüstungs-Panel neu zeichnen (kein Scroll-Sprung) + Set-Boni sofort aktualisieren. */
export function renderGearPanel() {
  const el = document.getElementById('adv-gear'); if (!el) { renderAdventure(); return; }
  el.innerHTML = gearPanelHTML();
  el.querySelectorAll('[data-equip]').forEach((b) => { b.onclick = () => gEquip(b.dataset.equip); });
  el.querySelectorAll('[data-ench]').forEach((b) => { b.onclick = (e) => { e.stopPropagation(); gEnchantItem(b.dataset.ench); }; });
  el.querySelectorAll('[data-gearsort]').forEach((b) => { b.onclick = () => { state.gearSort = b.dataset.gearsort; renderGearPanel(); }; });
  el.querySelectorAll('[data-gearfilter]').forEach((b) => { b.onclick = () => { state.gearFilter = b.dataset.gearfilter; renderGearPanel(); }; });
}
/* Arena-Statuswerte (Wissen/Geistesblitz/Münzen) live nachziehen, wenn sich Ausrüstung ändert. */
export function refreshArenaStats() {
  const bar = document.getElementById('adv-statsbar'); if (!bar) return;
  const st = gStats();
  const sp = bar.querySelectorAll('span');
  if (sp[0]) sp[0].innerHTML = `<img src="assets/icon_coin.png" alt="">${Math.round(st.coinsPerMin)} Wissensmünzen/min`;
  if (sp[1]) sp[1].textContent = `📖 Wissen ${Math.round(st.knowledge)}`;
  if (sp[2]) sp[2].textContent = `✨ Geistesblitz ${Math.round(st.crit)} %`;
}
export const ITEM_TIER = ['Gewöhnlich', 'Selten', 'Episch', 'Legendär', 'Mythisch'];
export const ITEM_TIER_CLS = ['r-common', 'r-rare', 'r-epic', 'r-legend', 'r-myth'];
export function itemBonusText(id, level) {
  const it = ADV_ITEMS[id]; const st = gItem(id); const v = (level + (st ? (st.ench || 0) : 0)) * it.per;
  return (it.per < 1 ? '+' + Math.round(v * 100) : '+' + Math.round(v * 100) / 100) + it.unit;
}
export const questBar = (cur, max) => `<div class="quest-bar"><i style="width:${Math.min(100, Math.round(cur / max * 100))}%"></i><span>${Math.min(cur, max)} / ${max}</span></div>`;
export function questCardHTML() {
  const q = gQuest(); const done = gQuestDone();
  const w = gWeekly(); const wdone = gWeeklyDone();
  const goals = q.tasks.map((t) => `<div class="quest-goal"><span>${questLabel(QUEST_POOL, t.type, t.goal)}</span>${questBar(t.prog, t.goal)}</div>`).join('');
  const daily = `<div class="game-panel quest-panel ${done && !q.claimed ? 'ready' : ''}">
    <h3 class="game-h">🎯 Tagesziele <small class="quest-sub">wechseln täglich</small></h3>
    <div class="quest-goals">${goals}</div>
    ${q.claimed ? '<div class="quest-claimed">✓ Heute schon abgeholt – morgen warten neue Ziele!</div>'
      : done ? '<button class="btn-primary quest-claim-btn" id="quest-claim">🎁 Belohnung abholen (Bonus-Truhe + Münzen)</button>'
        : '<div class="quest-note">Schaffe beide Ziele für eine <b>Bonus-Schatztruhe</b> + Münzen.</div>'}
  </div>`;
  const weekly = `<div class="game-panel quest-panel weekly ${wdone && !w.claimed ? 'ready' : ''}">
    <h3 class="game-h">🗓 Wochenziel <small class="quest-sub">größere Belohnung</small></h3>
    <div class="quest-goals"><div class="quest-goal"><span>${questLabel(WEEKLY_POOL, w.task.type, w.task.goal)}</span>${questBar(w.task.prog, w.task.goal)}</div></div>
    ${w.claimed ? '<div class="quest-claimed">✓ Diese Woche geschafft – nächste Woche geht’s weiter!</div>'
      : wdone ? '<button class="btn-primary quest-claim-btn" id="weekly-claim">🏆 Belohnung abholen (garantiert Legendär + 💎)</button>'
        : '<div class="quest-note">Belohnung: ein <b>garantiert legendäres Item</b> + Diamanten + Münzen.</div>'}
  </div>`;
  return daily + weekly;
}
export function gClaimQuest() {
  const g = G(); const q = gQuest();
  if (!gQuestDone() || q.claimed) return;
  q.claimed = true; g.chests += 1;
  const bonus = Math.max(50, Math.round(gStats().coinsPerMin * 12));
  state.progress.coins += bonus;
  SFX.unlock(); confetti(); saveProgress(); renderCurrency();
  pushPop({ img: 'assets/icon_coin.png', eyebrow: 'Tagesziel geschafft!', name: 'Bonus-Belohnung', reward: `🎁 +1 Truhe · +${bonus} Münzen` });
  renderAdventure();
}
export function gClaimWeekly() {
  const g = G(); const w = gWeekly();
  if (!gWeeklyDone() || w.claimed) return;
  w.claimed = true;
  // garantiert legendär: bevorzugt ein NOCH NICHT maxed Item um 5 Kopien verbessern (sonst Rest → Münzen)
  const all = Object.keys(ADV_ITEMS);
  const notMax = all.filter((k) => { const it = gItem(k); return !it || it.level < ITEM_MAX; });
  const id = (notMax.length ? notMax : all)[Math.floor(Math.random() * (notMax.length ? notMax.length : all.length))];
  const res = gAddItemCopies(id, 5);
  if (res.overflow > 0) state.progress.coins += res.overflow * 60;   // maxed → Überschuss als Münzen, nicht verfallen
  state.progress.gems += 2; state.progress.coins += 200;
  SFX.win(); confetti(); saveProgress(); renderCurrency();
  pushPop({ img: RPG(ADV_ITEMS[id].img), eyebrow: 'Wochenziel geschafft! 🏆', name: `${ADV_ITEMS[id].name} · Stufe ${res.level}`, reward: '+2 💎 · +200 Münzen' });
  renderAdventure();
}
export function gearPanelHTML() {
  const g = G();
  const owned = Object.keys(g.items).filter((id) => gItem(id));
  const eq = g.equip || {};
  const st = gStats();
  // Ausrüstungs-Plätze: 2 pro Kategorie
  const slots = Object.keys(ADV_SLOTS).map((slot) => {
    const arr = Array.isArray(eq[slot]) ? eq[slot] : (eq[slot] ? [eq[slot]] : []);
    const cells = Array.from({ length: SLOT_CAP }, (_, i) => {
      const id = arr[i]; const it = id ? ADV_ITEMS[id] : null; const lv = id ? gItem(id).level : 0;
      return `<div class="gear-slot ${id ? ITEM_TIER_CLS[lv - 1] : 'empty'}" ${id ? `data-equip="${id}" title="Ablegen"` : ''}>
        ${it ? `<img src="${RPG(it.img)}" alt=""><span class="gear-slot-lv">St. ${lv}</span>${it.group ? `<span class="gear-slot-grp">${ADV_GROUPS[it.group].icon}</span>` : ''}` : '<span class="gear-slot-empty">leer</span>'}
      </div>`;
    }).join('');
    return `<div class="gear-cat"><span class="gear-slot-lbl">${ADV_SLOTS[slot]}</span><div class="gear-cat-slots">${cells}</div></div>`;
  }).join('');
  // Set-Boni-Übersicht
  const active = {}; st.sets.forEach((s) => active[s.id] = s);
  const setBadges = Object.keys(ADV_GROUPS).map((gid) => {
    const grp = ADV_GROUPS[gid]; const a = active[gid]; const full = groupMaxWear(gid);
    const cnt = gEquipped().filter((id) => gItem(id) && ADV_ITEMS[id].group === gid).length;
    const val = (v) => grp.stat === 'knowledge' || grp.stat === 'coin' ? '+' + Math.round(v * 100) : '+' + v;
    const setSlots = [...new Set(Object.keys(ADV_ITEMS).filter((id) => ADV_ITEMS[id].group === gid).map((id) => ADV_ITEMS[id].slot))]
      .sort((x, y) => Object.keys(ADV_SLOTS).indexOf(x) - Object.keys(ADV_SLOTS).indexOf(y));
    const isFull = a && a.tier === 4;
    const bonTxt = a ? (isFull ? '★ Komplett · ' : '✓ ') + val(a.bon) + grp.unit : `2er: ${val(grp.b2) + grp.unit}`;
    return `<div class="set-badge ${a ? 'on' : ''} ${isFull ? 'full' : ''}" title="${esc(grp.name)} – komplett (${full} Teile): ${val(grp.bFull) + grp.unit}">
      <div class="set-badge-top"><span class="set-ic">${grp.icon}</span><span class="set-name">${esc(grp.name)}</span><span class="set-cnt">${cnt}/${full}</span></div>
      <div class="set-badge-slots">${setSlots.map((s) => `<span class="slot-chip mini slot-${s}">${SLOT_ICON[s]}</span>`).join('')}</div>
      <span class="set-bon">${bonTxt}</span>
    </div>`;
  }).join('');
  // Sortier-/Filterleiste
  const sort = state.gearSort || 'set';
  const filt = state.gearFilter || 'all';
  const SORTS = { set: 'Set', rarity: 'Seltenheit', level: 'Stufe', name: 'Name' };
  const sortBtns = Object.keys(SORTS).map((k) => `<button class="gear-chip ${sort === k ? 'on' : ''}" data-gearsort="${k}">${SORTS[k]}</button>`).join('');
  const filtBtns = [['all', 'Alle'], ['head', SLOT_ICON.head + ' Kopf'], ['face', SLOT_ICON.face + ' Gesicht'], ['trinket', SLOT_ICON.trinket + ' Talisman']]
    .map(([k, lbl]) => `<button class="gear-chip ${filt === k ? 'on' : ''}" data-gearfilter="${k}">${lbl}</button>`).join('');
  const toolbar = `<div class="gear-toolbar"><div class="gear-tb-row"><span class="gear-tb-lbl">Sortieren</span>${sortBtns}</div><div class="gear-tb-row"><span class="gear-tb-lbl">Platz</span>${filtBtns}</div></div>`;
  // Item-Karte
  const invItemHTML = (id) => {
    const it = ADV_ITEMS[id]; const s = gItem(id); const lv = s.level; const ench = s.ench || 0; const maxed = lv >= ITEM_MAX;
    const need = maxed ? 0 : ITEM_COST[lv]; const prog = maxed ? 100 : Math.round(s.copies / need * 100);
    const equipped = gIsEquipped(id);
    const stars = '★'.repeat(lv) + '☆'.repeat(ITEM_MAX - lv);
    const canEnch = ench < ENCH_MAX; const enchCost = canEnch ? ENCH_COST[ench] : 0;
    return `<button class="inv-item ${ITEM_TIER_CLS[lv - 1]} ${equipped ? 'equipped' : ''}" data-equip="${id}" title="${equipped ? 'Ablegen' : 'Ausrüsten'} (${esc(ADV_SLOTS[it.slot])})">
      ${slotChip(it.slot)}
      <span class="inv-ench-btn ${canEnch ? '' : 'maxed'}" data-ench="${id}" title="${canEnch ? 'Verzaubern (+Bonus) · ' + enchCost + ' 💎' : 'Maximal verzaubert'}">✨${canEnch ? '' : '<i>MAX</i>'}</span>
      <div class="inv-icon"><img src="${RPG(it.img)}" alt="">${equipped ? '<span class="inv-eq">✓</span>' : ''}${ench > 0 ? `<span class="inv-ench">+${ench}</span>` : ''}</div>
      <div class="inv-name">${esc(it.name)}</div>
      <div class="inv-tier">${ITEM_TIER[lv - 1]}${ench > 0 ? ' ✨+' + ench : ''} · ${itemBonusText(id, lv)}</div>
      <div class="inv-stars">${stars}</div>
      <div class="inv-prog"><i style="width:${prog}%"></i></div>
      <div class="inv-progtxt">${maxed ? 'MAX' : s.copies + ' / ' + need}</div>
    </button>`;
  };
  const pass = (id) => filt === 'all' || ADV_ITEMS[id].slot === filt;
  let inv;
  if (!owned.length) {
    inv = `<div class="adv-gear-empty"><img class="adv-gear-empty-img" src="assets/rpg/item_book.png" alt=""><div><b>Noch keine Lernbegleiter – öffne Schatztruhen!</b><p class="page-sub" style="margin:2px 0 0">Doppelte Items verbessern sich: Stufe 1→2 braucht 2, dann 3, 5, 10 Kopien (max. Stufe 5).</p></div></div>`;
  } else if (sort === 'set') {
    inv = Object.keys(ADV_GROUPS).map((gid) => {
      const ids = owned.filter((id) => ADV_ITEMS[id].group === gid && pass(id));
      if (!ids.length) return '';
      const slotsInSet = [...new Set(ids.map((id) => ADV_ITEMS[id].slot))].sort((a, b) => Object.keys(ADV_SLOTS).indexOf(a) - Object.keys(ADV_SLOTS).indexOf(b));
      return `<div class="inv-group"><div class="inv-group-h">${ADV_GROUPS[gid].icon} ${esc(ADV_GROUPS[gid].name)} <small>${ids.length} Teil${ids.length > 1 ? 'e' : ''}</small><span class="inv-group-slots">${slotsInSet.map(slotChip).join('')}</span></div><div class="gear-inv">${ids.map(invItemHTML).join('')}</div></div>`;
    }).join('') || '<p class="page-sub">Keine Items für diesen Platz.</p>';
  } else {
    let list = owned.filter(pass);
    if (sort === 'level') list.sort((a, b) => gItem(b).level - gItem(a).level || gItem(b).ench - gItem(a).ench);
    else if (sort === 'name') list.sort((a, b) => ADV_ITEMS[a].name.localeCompare(ADV_ITEMS[b].name));
    else if (sort === 'rarity') list.sort((a, b) => gItem(b).level - gItem(a).level);   // Stufe = Seltenheitsrang
    inv = list.length ? `<div class="gear-inv">${list.map(invItemHTML).join('')}</div>` : '<p class="page-sub">Keine Items für diesen Platz.</p>';
  }
  return `<p class="gear-hint">Trage <b>2 Items</b> pro Kopf, Gesicht &amp; Talisman. Ein <b>komplettes Set</b> gibt den stärksten Bonus. Mit ✨ verzauberst du ein Item für 💎.</p>
    <div class="gear-slots">${slots}</div>
    <div class="set-badges">${setBadges}</div>
    ${toolbar}
    <div class="inv-groups">${inv}</div>`;
}

